import { BoxConnectionItem, BoxDetailConnectionProps } from '@/box/interface'
import { secureConnectionTypes } from '@/shared/DefaultValues'
import {
  connectionModeItems,
  getOptions,
  mqttConnectionTypes,
  mqttVersionItems
} from '@/shared/SelectData'
import { formatMqttUrl, getFormatMessage } from '@/utils/utils'
import { FileOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  Upload
} from 'antd'
import { RcFile } from 'antd/lib/upload'
import React, { useEffect, useState } from 'react'
import { Title } from '../shared/Title'
import { formItemLayout, labelCol6 } from '../shared/Shared'

export const BoxDetailConnection: React.FC<BoxDetailConnectionProps> = (
  props: BoxDetailConnectionProps
) => {
  const {
    editItem,
    title,
    formResult,
    colSpan,
    disabled,
    onSave,
    onFormValidate
  } = props
  const [connectionForm] = Form.useForm<BoxConnectionItem>()
  const [connectionMode, setConnectionMode] = useState<string>('mqtt')
  const [connectionType, setConnectionType] = useState<string>('ws://')
  const [certType, setCertType] = useState<number>(0)
  const [mqttServer, setMqttServer] = useState<string>()
  const [loading, setLoading] = useState<boolean>(false)
  const formatMessage = getFormatMessage()

  const defaultCol = 8

  const hostTypeSelect = (
    <Select
      value={connectionType}
      className='select-before'
      onChange={setConnectionType}
      getPopupContainer={(trigger) => trigger.parentNode}
    >
      {mqttConnectionTypes.map((x) => (
        <Select.Option key={x.value} value={x.value}>
          {x.text}
        </Select.Option>
      ))}
    </Select>
  )

  const caCrtUploadProps = {
    showUploadList: false,
    multiple: false,
    accept: '.crt',
    beforeUpload: async (file: RcFile) => {
      const text = await file.text()
      const item = connectionForm.getFieldsValue()
      item.caCrt = text
      item.caCrtName = file.name
      connectionForm.setFieldsValue(item)
      return false
    }
  }

  const clientCrtUploadProps = {
    showUploadList: false,
    multiple: false,
    accept: '.crt',
    beforeUpload: async (file: RcFile) => {
      const text = await file.text()
      const item = connectionForm.getFieldsValue()
      item.clientCrt = text
      item.clientCrtName = file.name
      connectionForm.setFieldsValue(item)
      return false
    }
  }

  const clientKeyUploadProps = {
    showUploadList: false,
    multiple: false,
    accept: '.crt',
    beforeUpload: async (file: RcFile) => {
      const text = await file.text()
      const item = connectionForm.getFieldsValue()
      item.clientKey = text
      item.clientKeyName = file.name
      connectionForm.setFieldsValue(item)
      return false
    }
  }

  const handleConnectionSave = async () => {
    if (!onSave) return
    try {
      const value = await connectionForm.validateFields()
      if (connectionMode == 'mqtt' && value.server) {
        value.server = formatMqttUrl(connectionType, value.server)
      }
      if (value.cleansession !== null && value.cleansession !== undefined) {
        value.cleansession = +value.cleansession!
      }
      setLoading(true)
      await onSave(value)
    } catch (err) {
      onFormValidate?.(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (editItem) {
      const mode = editItem.mode || connectionMode
      const server = editItem.server
      if (mode == 'mqtt' && server && server.includes('://')) {
        const type = `${server.substring(0, server.indexOf('/'))}//`
        setConnectionType(type)
        const svr = server.replace(type, '')
        setMqttServer(svr)
        editItem.server = svr
      }
      setConnectionMode(mode)
      setCertType(editItem.crtType || 0)
      connectionForm.setFieldsValue(editItem)
    }
  }, [editItem])

  useEffect(() => {
    if (formResult) {
      handleConnectionSave()
    }
  }, [formResult])

  return (
    <Form {...formItemLayout} form={connectionForm} labelAlign='right'>
      {title && (
        <Title
          level={4}
          extra={
            <Button
              type='primary'
              disabled={disabled}
              loading={loading}
              onClick={handleConnectionSave}
            >
              {formatMessage({ id: 'app.box.detail.edit.save' })}
            </Button>
          }
        >
          {formatMessage({ id: 'app.box.detail.edit.connection' })}
        </Title>
      )}
      <Row>
        <Col span={colSpan || defaultCol}>
          <Form.Item
            name='mode'
            label={formatMessage({ id: 'app.box.detail.edit.mode' })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'app.box.detail.edit.mode.message'
                })
              }
            ]}
          >
            <Select
              disabled={disabled}
              onChange={(value: string) => setConnectionMode(value)}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {getOptions(connectionModeItems)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={colSpan || defaultCol}>
          {connectionMode === 'mqtt' ? (
            <Form.Item
              label={formatMessage({ id: 'app.box.detail.edit.server' })}
              name='server'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.detail.edit.server.message'
                  })
                }
              ]}
            >
              <Input
                value={mqttServer}
                addonBefore={hostTypeSelect}
                disabled={disabled}
                onChange={(e) => setMqttServer(e.target.value)}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={formatMessage({ id: 'app.box.detail.edit.server' })}
              name='server'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.detail.edit.server.message'
                  })
                }
              ]}
            >
              <Input disabled={disabled} />
            </Form.Item>
          )}
        </Col>
        {connectionMode === 'mqtt' && (
          <Col span={colSpan || defaultCol}>
            <Form.Item
              name='mqttVersion'
              label={formatMessage({ id: 'app.box.detail.edit.mqttVersion' })}
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.detail.edit.mqttVersion.message'
                  })
                }
              ]}
            >
              <Select
                disabled={disabled}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {getOptions(mqttVersionItems)}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      {connectionMode === 'mqtt' && (
        <Row>
          <Col span={colSpan || defaultCol}>
            <Form.Item
              name='connectTimeout'
              label={formatMessage({
                id: 'app.box.detail.edit.connectTimeout'
              })}
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.detail.edit.connectTimeout.message'
                  })
                }
              ]}
            >
              <InputNumber
                disabled={disabled}
                style={{ width: '100%' }}
                min={10}
                max={120}
              />
            </Form.Item>
          </Col>
          <Col span={colSpan || defaultCol}>
            <Form.Item
              name='keepAlive'
              label={formatMessage({ id: 'app.box.detail.edit.keepAlive' })}
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.detail.edit.keepAlive.message'
                  })
                }
              ]}
            >
              <InputNumber
                disabled={disabled}
                style={{ width: '100%' }}
                min={10}
                max={86400}
              />
            </Form.Item>
          </Col>
          <Col span={colSpan || defaultCol}>
            <Form.Item
              name='cleansession'
              label={formatMessage({ id: 'app.box.detail.edit.cleansession' })}
              valuePropName='checked'
            >
              <Switch disabled={disabled} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {connectionMode === 'mqtt' && (
        <Row>
          {secureConnectionTypes.includes(connectionType || '') && (
            <Col span={colSpan || defaultCol}>
              <Form.Item
                label={formatMessage({ id: 'app.box.detail.edit.certType' })}
              >
                <Radio.Group
                  value={certType}
                  disabled={disabled}
                  onChange={(e) => {
                    setCertType(e.target.value)
                  }}
                >
                  <Radio value={0}>
                    {formatMessage({ id: 'app.box.mqtt.certType.0' })}
                  </Radio>
                  <Radio value={1}>
                    {formatMessage({ id: 'app.box.mqtt.certType.1' })}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          )}
        </Row>
      )}

      {connectionMode === 'mqtt' &&
        secureConnectionTypes.includes(connectionType || '') && (
          <Row>
            {certType == 1 && (
              <Col span={colSpan || defaultCol}>
                <Form.Item
                  label={formatMessage({ id: 'app.box.detail.edit.caCrt' })}
                  name='caCrtName'
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.box.detail.edit.caCrt.message'
                      })
                    }
                  ]}
                >
                  <Input
                    readOnly
                    className='ant-input-search ant-input-search-with-button'
                    addonAfter={
                      <Upload {...caCrtUploadProps}>
                        <Button
                          type='primary'
                          className='ant-input-search-button'
                        >
                          <FileOutlined />
                        </Button>
                      </Upload>
                    }
                  />
                </Form.Item>

                <Form.Item style={{ display: 'none' }} name='caCrt'>
                  <Input />
                </Form.Item>
              </Col>
            )}

            {certType == 1 && (
              <Col span={colSpan || defaultCol}>
                <Form.Item
                  {...labelCol6}
                  label={formatMessage({ id: 'app.box.detail.edit.clientCrt' })}
                  name='clientCrtName'
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.box.detail.edit.clientCrt.message'
                      })
                    }
                  ]}
                >
                  <Input
                    readOnly
                    className='ant-input-search ant-input-search-with-button'
                    addonAfter={
                      <Upload {...clientCrtUploadProps}>
                        <Button
                          type='primary'
                          className='ant-input-search-button'
                        >
                          <FileOutlined />
                        </Button>
                      </Upload>
                    }
                  />
                </Form.Item>

                <Form.Item style={{ display: 'none' }} name='clientCrt'>
                  <Input />
                </Form.Item>
              </Col>
            )}

            {certType == 1 && (
              <Col span={colSpan || defaultCol}>
                <Form.Item
                  {...labelCol6}
                  label={formatMessage({ id: 'app.box.detail.edit.clientKey' })}
                  name='clientKeyName'
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.box.detail.edit.clientKey.message'
                      })
                    }
                  ]}
                >
                  <Input
                    readOnly
                    className='ant-input-search ant-input-search-with-button'
                    addonAfter={
                      <Upload {...clientKeyUploadProps}>
                        <Button
                          type='primary'
                          className='ant-input-search-button'
                        >
                          <FileOutlined />
                        </Button>
                      </Upload>
                    }
                  />
                </Form.Item>

                <Form.Item style={{ display: 'none' }} name='clientKey'>
                  <Input />
                </Form.Item>
              </Col>
            )}
          </Row>
        )}
    </Form>
  )
}
